import { FC } from "react";
import DialogActions from "@mui/material/DialogActions";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import { Alert, Box, ButtonPropsColorOverrides, Stack } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import {
  DialogCustomButton,
  DialogCustomContent,
  DialogCustomTitle,
} from "./styles";
import { getDarker } from "../../../verificationRequest/utils/getColorDarker";

type ThemeColors = OverridableStringUnion<
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning",
  ButtonPropsColorOverrides
>;

export type IConfirmationDialog = {
  onClose: (props?: any) => void;
  isOpen: boolean;
  title?: string;
  description?: any;
  confirmAction: (args?: any) => void;
  confirmText?: string;
  declineText?: string;
  titleColor?: string;
  descriptionColor?: string;
  confirmButtonColor?: ThemeColors | string;
  error?: any;
  disabled?: boolean;
  withoutDecline?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  papeprWidth?: number;
};

const allowedColors: ThemeColors[] = [
  "inherit",
  "primary",
  "secondary",
  "success",
  "error",
  "info",
  "warning",
];

function isValidMuiColor(color: string | undefined): color is ThemeColors {
  return allowedColors.includes(color as ThemeColors);
}

export const ConfirmationDialog: FC<IConfirmationDialog> = ({
  isOpen,
  onClose,
  title,
  description,
  confirmAction,
  confirmText = "Ok",
  declineText = "Cancel",
  titleColor,
  descriptionColor,
  confirmButtonColor,
  error,
  disabled,
  withoutDecline = false,
  maxWidth = "sm",
  papeprWidth = 409,
  children,
}) => {
  return (
    <Dialog
      sx={{
        "& .MuiDialog-paper": {
          width: `${papeprWidth}px`,
          borderRadius: "16px",
        },
      }}
      maxWidth={maxWidth}
      open={isOpen}
      id='document-review-dialog'
      keepMounted
    >
      <DialogCustomTitle sx={{ color: titleColor }}>{title}</DialogCustomTitle>
      <DialogCustomContent sx={{ color: descriptionColor }}>
        {description}
      </DialogCustomContent>
      {children ? (
        <Stack sx={{ padding: "0 32px 32px" }}>{children}</Stack>
      ) : null}
      <DialogActions sx={{ padding: "0 32px 12px" }}>
        <Stack
          flexDirection='row'
          alignItems='center'
          justifyContent={!withoutDecline ? "space-between" : "center"}
          width='100%'
        >
          {!withoutDecline ? (
            <Box>
              <DialogCustomButton
                autoFocus
                onClick={onClose}
                disabled={disabled}
                variant='outlined'
                sx={{ width: papeprWidth / 2.486 }}
              >
                {declineText}
              </DialogCustomButton>
            </Box>
          ) : null}

          <Box>
            <DialogCustomButton
              onClick={confirmAction}
              disabled={disabled}
              variant='contained'
              color={
                isValidMuiColor(confirmButtonColor)
                  ? confirmButtonColor
                  : "inherit"
              }
              sx={{
                color: "#fff",
                backgroundColor: confirmButtonColor ?? "inherit",
                "&:hover": {
                  backgroundColor: confirmButtonColor
                    ? getDarker(confirmButtonColor, 0.7)
                    : "inherit",
                },
                width: papeprWidth / 2.486,
              }}
            >
              {confirmText}
            </DialogCustomButton>
          </Box>
        </Stack>
      </DialogActions>
      <DialogCustomContent sx={{ color: descriptionColor }}>
        {error && <Alert severity='error'>{error.toString()}</Alert>}
      </DialogCustomContent>
    </Dialog>
  );
};
